import { useEffect, useId, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { format as formatDate, formatISO, parseISO } from 'date-fns';
import { enUS, es, ptBR } from 'react-day-picker/locale';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  DATE_PICKER_COMPACT,
  DatePickerInput,
} from '@/components/ui/date-picker-input';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  useShippingFormExport,
  useShippingFormExportExcel,
} from '@/services/reports';

function useSchema() {
  const { t } = useTranslation();

  return useMemo(() => {
    const requiredText = z.string().trim().min(1, t('common.required'));
    const maxLength = (count: number) =>
      t('shipping.exportForm.maxLength', { count });

    return z.object({
      titlePrefix: z.string().trim().max(50, maxLength(50)),
      showSoldTo: z.boolean(),
      showStrawDisclaimer: z.boolean(),
      veterinarian: requiredText.max(100, maxLength(100)),
      accreditationNumber: requiredText.max(20, maxLength(20)),
      date: requiredText,
      country: requiredText.max(60, maxLength(60)),
      seal: requiredText.max(100, maxLength(100)),
      exporter: requiredText.max(60, maxLength(60)),
      isEu: z.boolean(),
      euEmbryoStatement: z.enum(['', 'Emb-A', 'Emb-B', 'Emb-D']),
      euSemenStatement: z.boolean(),
    });
  }, [t]);
}

type FormValues = z.infer<ReturnType<typeof useSchema>>;

function getDefaultValues(): FormValues {
  return {
    titlePrefix: '',
    showSoldTo: false,
    showStrawDisclaimer: false,
    veterinarian: '',
    accreditationNumber: '',
    date: formatISO(new Date(), { representation: 'date' }),
    country: '',
    seal: '',
    exporter: '',
    isEu: false,
    euEmbryoStatement: '',
    euSemenStatement: false,
  };
}

export const ShippingFormExportDialog = ({
  open,
  shipmentIds,
  format,
  onClose,
}: {
  open: boolean;
  shipmentIds: string[];
  format: 'pdf' | 'excel';
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const formId = useId();
  const schema = useSchema();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(),
  });
  const [generatePdf] = useShippingFormExport();
  const [generateExcel] = useShippingFormExportExcel();
  const { isSubmitting, errors } = form.formState;
  const error = errors.root?.message;
  const { reset } = form;

  useEffect(() => {
    if (open) {
      reset(getDefaultValues());
    }
  }, [open, reset]);

  const submit = form.handleSubmit(
    async ({ titlePrefix, accreditationNumber, isEu, ...options }) => {
      form.clearErrors('root');
      try {
        const generate = format === 'excel' ? generateExcel : generatePdf;
        await generate({
          ...options,
          title: titlePrefix
            ? `${titlePrefix} Export Shipment`
            : 'Export Shipment',
          euEmbryoStatement: isEu ? options.euEmbryoStatement : '',
          euSemenStatement: isEu && options.euSemenStatement,
          veterinarian: `${options.veterinarian}, DVM #${accreditationNumber}`,
          shipmentIds,
        });
        onClose();
      } catch (cause) {
        console.error('Error generating export shipping form', cause);
        form.setError('root', {
          message:
            cause instanceof Error
              ? cause.message
              : t('shipping.exportForm.error'),
        });
      }
    },
  );

  return (
    <Dialog
      open={open && !isSubmitting}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) {
          onClose();
        }
      }}
    >
      <DialogContent className="md:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('shipping.exportForm.title')}{' '}
            {t(
              format === 'pdf'
                ? 'shipping.exportForm.pdf'
                : 'shipping.exportForm.excel',
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogBody
          as="form"
          id={formId}
          onSubmit={submit}
          className="space-y-5 p-4"
        >
          <fieldset disabled={isSubmitting} className="space-y-5">
            <FormProvider {...form}>
              <TextField
                name="titlePrefix"
                label={t('shipping.exportForm.shipmentTitle')}
                suffix="Export Shipment"
              />
              <div className="grid items-start gap-4 sm:grid-cols-2">
                <div className="grid gap-4">
                  <TextField
                    name="veterinarian"
                    label={t('shipping.exportForm.veterinarian')}
                  />
                  <TextField
                    name="accreditationNumber"
                    label={t('shipping.exportForm.accreditationNumber')}
                  />
                  <DateField />
                  <TextField
                    name="seal"
                    label={t('shipping.exportForm.seal')}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FormSwitch
                      name="showSoldTo"
                      label={t('shipping.exportForm.soldTo')}
                    />
                    <FormSwitch
                      name="showStrawDisclaimer"
                      label={t('shipping.exportForm.strawDisclaimer')}
                    />
                  </div>
                </div>
                <div className="grid gap-4">
                  <TextField
                    name="country"
                    label={t('shipping.exportForm.country')}
                  />
                  <TextField
                    name="exporter"
                    label={t('shipping.exportForm.exporter')}
                  />
                  <EuStatements />
                </div>
              </div>
            </FormProvider>
          </fieldset>
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={onClose}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={isSubmitting || !shipmentIds.length}
          >
            {isSubmitting
              ? t('shipping.exportForm.generating')
              : t('shipping.exportForm.generate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type TextFieldProps = {
  name:
    | 'titlePrefix'
    | 'veterinarian'
    | 'accreditationNumber'
    | 'seal'
    | 'country'
    | 'exporter';
  label: string;
  suffix?: string;
};

function TextField({ name, label, suffix }: TextFieldProps) {
  const id = useId();
  const {
    register,
    formState: { errors },
  } = useFormContext<FormValues>();
  const error = errors[name];

  return (
    <Field data-invalid={!!error}>
      <FieldLabel htmlFor={id} className="gap-0">
        {label}
        {name !== 'titlePrefix' && (
          <span aria-hidden="true" className="text-destructive">
            *
          </span>
        )}
      </FieldLabel>
      <div className="flex items-center gap-2">
        <Input
          {...register(name)}
          id={id}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        {suffix && <span className="shrink-0 text-sm">{suffix}</span>}
      </div>
      <FieldError id={`${id}-error`} errors={[error]} />
    </Field>
  );
}

function FormSwitch({
  name,
  label,
  disabled = false,
}: {
  name: 'showSoldTo' | 'showStrawDisclaimer' | 'isEu' | 'euSemenStatement';
  label: string;
  disabled?: boolean;
}) {
  const id = useId();
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<FormValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Field>
          <FieldLabel id={`${id}-label`} htmlFor={id} className="gap-0">
            {label}
          </FieldLabel>
          <div className="flex h-8 items-center">
            <Switch
              id={id}
              aria-labelledby={`${id}-label`}
              disabled={isSubmitting || disabled}
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          </div>
        </Field>
      )}
    />
  );
}

function DateField() {
  const id = useId();
  const { t, i18n } = useTranslation();
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<FormValues>();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const locale = language === 'es' ? es : language === 'ptbr' ? ptBR : enUS;

  return (
    <Controller
      name="date"
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Field data-invalid={!!error}>
          <FieldLabel htmlFor={id} className="gap-0">
            {t('shipping.exportForm.date')}
            <span aria-hidden="true" className="text-destructive">
              *
            </span>
          </FieldLabel>
          <DatePickerInput
            id={id}
            name={field.name}
            className={DATE_PICKER_COMPACT}
            initialDate={field.value ? parseISO(field.value) : undefined}
            inputRef={field.ref}
            locale={locale}
            disabled={isSubmitting}
            aria-invalid={!!error}
            aria-describedby={error ? `${id}-error` : undefined}
            onBlur={field.onBlur}
            onChange={(date) =>
              field.onChange(date ? formatDate(date, 'yyyy-MM-dd') : '')
            }
          />
          <FieldError id={`${id}-error`} errors={[error]} />
        </Field>
      )}
    />
  );
}

function EuStatements() {
  const { t } = useTranslation();
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<FormValues>();
  const isEu = useWatch({ control, name: 'isEu' });
  const disabled = isSubmitting || !isEu;

  return (
    <div className="grid gap-4">
      <FormSwitch name="isEu" label={t('shipping.exportForm.isEu')} />
      <EuEmbryoSelect disabled={disabled} />
      <FormSwitch
        name="euSemenStatement"
        label={t('shipping.exportForm.euSemenStatement')}
        disabled={disabled}
      />
    </div>
  );
}

function EuEmbryoSelect({ disabled }: { disabled: boolean }) {
  const id = useId();
  const { t } = useTranslation();
  const { control } = useFormContext<FormValues>();
  const options = [
    { value: null, label: t('shipping.exportForm.none') },
    { value: 'Emb-A', label: t('shipping.exportForm.embA') },
    { value: 'Emb-B', label: t('shipping.exportForm.embB') },
    { value: 'Emb-D', label: t('shipping.exportForm.embD') },
  ];

  return (
    <Controller
      name="euEmbryoStatement"
      control={control}
      render={({ field }) => (
        <Field>
          <FieldLabel htmlFor={id} className="gap-0">
            {t('shipping.exportForm.euEmbryoStatement')}
          </FieldLabel>
          <Select
            ref={field.ref}
            name={field.name}
            value={field.value || null}
            onChange={(value) => field.onChange(value ?? '')}
            items={options}
            disabled={disabled}
          >
            <SelectTrigger id={id} onBlur={field.onBlur}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((item) => (
                <SelectItem key={item.value ?? 'none'} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
    />
  );
}
